import * as React from "react";
import { useCallback, useRef, useState } from "react";
import styled from "@mui/material/styles/styled";
import { DocQuestion } from "./DocQuestion";
import { useTranslate } from "../../hooks/useTranslate";
import { GenericButton } from "../uiControllers/genericButton";
import { QuestionsIF } from "./QuestionsIF";
import {
  buildFreeTextQF,
  getFreeTextFromQF,
} from "fe-common/logic/qualifiedFeature/freeText";
import {
  PatientInput,
  PatientTextAreaField,
} from "../uiControllers/patientTextField";
import clsx from "clsx";
//region [[ Styles ]]
const FreeTextQuestionView = styled("div")(({ theme }) => ({
  width: "100%",
  "& textarea": {
    padding: "15px",
  },
}));

const TextArea = styled(PatientTextAreaField)({
  resize: "none",
  fontFamily: "'Inter', sans-serif",
  fontSize: "16px",
  borderRadius: "10px",
  marginTop: "5px",
});

const DoneButtonContainer = styled("div")({
  display: "flex",
  alignItems: "flex-end",
  flexDirection: "column",
});

const SingleLineText = styled(PatientInput)({
  borderRadius: "4px",
  marginBottom: "5px",
  height: "40px",
  width: "100%",
});
//endregion [[ Styles ]]
//region [[ Props ]]
export interface FreeTextQuestionProps extends QuestionsIF {
  className?: string;
  questionText: string;
  isFreeTextSuggestion?: boolean;
  minRows?: number;
  maxRows?: number;
  placeHolder?: string;
  miniView?: boolean;
}
//endregion [[ Props ]]
//region [[ Functions ]]
//endregion [[ Functions ]]
export const FreeTextQuestion = ({
  questionText,
  minRows = 5,
  maxRows = 5,
  miniView = false,
  placeHolder,
  ...props
}: FreeTextQuestionProps) => {
  const [txt, setTxt] = useState(
    getFreeTextFromQF(props.previousAnswer?.qfs?.[0]) || "",
  );
  const { translate } = useTranslate();
  const timeout = useRef(null);

  const isAnswered = props.answer;
  const { onAnswer, isFreeTextSuggestion, question } = props;

  const handleSubmit = useCallback(
    (txt: string) => {
      // In forms we might skip some answers, if no text was provided report it as null so the form answer would know to skip it
      if (!txt && miniView) {
        onAnswer(null);
      } else {
        let answer = buildFreeTextQF(txt);
        if (isFreeTextSuggestion && question?.suggestions?.[0]) {
          // If it's a free text suggestion vs free text question, make sure to keep the suggested feature as the answered feature
          const firstSuggestion = question.suggestions?.[0];
          answer = {
            ...answer,
            feature: firstSuggestion.suggestion.feature,
            skip: !txt || txt === "",
          };
        }
        onAnswer([answer as any]);
      }
    },
    [onAnswer, isFreeTextSuggestion, question, miniView],
  );

  const onChange = (e) => {
    const text = e.target.value;
    setTxt(text);
    if (miniView) {
      if (timeout.current) {
        clearTimeout(timeout.current);
      }
      timeout.current = setTimeout(() => handleSubmit(text), 100);
    }
  };

  const isTextArea = maxRows > 1;

  return (
    <FreeTextQuestionView
      className={clsx(props.className, "free-text-question")}
    >
      <DocQuestion text={questionText} miniView={miniView} />
      {!isAnswered && (
        <>
          {isTextArea && (
            <TextArea
              onChange={onChange}
              aria-label="empty textarea"
              minRows={minRows}
              maxRows={maxRows}
              placeholder={placeHolder || ""}
              defaultValue={getFreeTextFromQF(props.previousAnswer?.qfs?.[0])}
            />
          )}
          {!isTextArea && (
            <SingleLineText onChange={onChange} placeholder={placeHolder} />
          )}
          {!miniView && (
            <DoneButtonContainer>
              <GenericButton
                className={"submit-button"}
                label={translate("ANSWER_DONE")}
                onClick={() => handleSubmit(txt)}
              />
            </DoneButtonContainer>
          )}
        </>
      )}
    </FreeTextQuestionView>
  );
};
